/* eslint-disable no-bitwise --
 * A regra existe para pegar `&`/`|` usados por engano no lugar de `&&`/`||`. Aqui os
 * operadores de bit SÃO o assunto: FNV-1a é definido em termos deles, e escrever a mesma
 * dispersão com aritmética seria mais lento, mais longo e menos legível. O escopo é este
 * arquivo, que não faz outra coisa. */

/**
 * Identidade visual de um contato quando NÃO existe foto (Fase 3).
 *
 * ── Por que isto existe ────────────────────────────────────────────────────────────────
 * A Cloud API da Meta não entrega foto de perfil do cliente, e isso não é limitação nossa:
 * é limitação dela, para todas as plataformas. O respond.io mantém o pedido aberto há anos
 * com a resposta oficial "this is a limitation set by Meta's team to all WhatsApp API
 * accounts", e o único contorno que existe no mundo é um cliente NÃO-OFICIAL de WhatsApp Web
 * — a mesma classe da Evolution, que já custou o banimento de um número desta operação.
 *
 * O respond.io — um dos maiores do mundo neste mercado — mantém o pedido em aberto com a
 * resposta oficial de que "the Meta API doesn't allow business platforms like ours to access
 * the contact's profile picture", e o contorno que ELES oferecem é upload manual. Ou seja: o
 * estado da arte do mercado é o que a Fase 3 já entregou.
 *
 * Então a pergunta certa deixa de ser "como pegar a foto" e passa a ser "como a vendedora
 * sabe com quem está falando". Para contato COM NOME, as iniciais respondem bem. O problema
 * é o contato que chega só com número — e ele é a maioria: 39 de 61 numa caixa real em
 * 25/08. Para todos eles as iniciais são o mesmo "+5". Isso não é identidade, é ruído com
 * aparência de informação — exatamente o defeito que a paleta de avatar já tinha e que o
 * redesign expurgou.
 *
 * ── As duas regras que governam o desenho ──────────────────────────────────────────────
 * 1. **Sem matiz.** A política de cor da plataforma é explícita: "avatares/identidade nossa
 *    = hueless (ink/tons de slate por hash, sem matiz)". Nada de arco-íris voltando pela
 *    porta dos fundos.
 * 2. **A chave é a IDENTIDADE, não o nome.** O código anterior sorteava o degrau por
 *    `name.length % 6`, e o próprio comentário dele explicava o problema: duas pessoas com
 *    nomes do mesmo tamanho recebem o mesmo visual, e a MESMA pessoa muda de visual quando
 *    alguém corrige o nome dela. Aqui a chave é o telefone (ou o identificador), que não
 *    muda quando o cadastro é editado.
 */

// FNV-1a de 32 bits: determinístico, estável entre navegadores e sem dependência.
// Não é cripto — é dispersão, e é só disso que se precisa.
export function hashDeIdentidade(chave) {
  const texto = String(chave ?? '');
  let h = 0x811c9dc5;
  for (let i = 0; i < texto.length; i += 1) {
    h ^= texto.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/**
 * O nome é só o telefone? Então não há nome.
 *
 * O importador de histórico carimba o número em E.164 como nome enquanto o real não chega,
 * e a agenda do celular pode nunca chegar (depende da coexistência). Comparar só os DÍGITOS
 * porque a mesma coisa aparece como `+5511...`, `5511...` ou com máscara.
 */
export function nomeEhOTelefone(nome, telefone) {
  const cru = String(nome ?? '').trim();
  if (!cru) return true;

  const soDigitos = valor => String(valor ?? '').replace(/\D/g, '');
  const n = soDigitos(cru);
  // ⚠️ Nome SEM dígito nenhum é nome de verdade. A primeira versão devolvia `true` aqui —
  // confundia "não tem dígitos" com "não tem nome" — e mandava "Larissa RH" para o identicon.
  // O teste pegou; a tela não teria pegado tão cedo, porque um identicon bonito no lugar das
  // iniciais não parece defeito.
  if (!n) return false;

  const t = soDigitos(telefone);
  if (t && n === t) return true;

  // Sem telefone para comparar: um "nome" que é só dígitos (com `+` ou máscara) também não é
  // nome escolhido por ninguém.
  return /^\+?[\d\s()\-.]+$/.test(cru);
}

const LADO = 5;
const METADE = Math.ceil(LADO / 2);

// Uma grade quase vazia (ou quase cheia) não distingue ninguém — vira um ponto ou um bloco.
//
// A primeira versão disto virava a coluna central de uma vez, e o teste pegou o furo: a
// coluna central move a densidade em no máximo 5, então uma grade com 2 acesas continuava
// quase vazia depois da "correção". Agora o reparo caminha por posições fixas até entrar na
// faixa — determinístico, converge sempre, e só toca nas grades extremas (raras), então não
// achata o espaço de desenhos distintos.
const DENSIDADE_MIN = 6;
const DENSIDADE_MAX = 19;

function ajustarDensidade(grade) {
  const acesas = () => grade.flat().filter(Boolean).length;
  let d = acesas();

  for (let y = 0; y < LADO; y += 1) {
    for (let x = 0; x < METADE; x += 1) {
      if (d >= DENSIDADE_MIN && d <= DENSIDADE_MAX) return grade;

      // Célula espelhada vale 2; a da coluna central vale 1.
      const peso = x === METADE - 1 ? 1 : 2;
      if (d < DENSIDADE_MIN && !grade[y][x]) {
        grade[y][x] = true;
        grade[y][LADO - 1 - x] = true;
        d += peso;
      } else if (d > DENSIDADE_MAX && grade[y][x]) {
        grade[y][x] = false;
        grade[y][LADO - 1 - x] = false;
        d -= peso;
      }
    }
  }

  return grade;
}

/**
 * Padrão espelhado de 5×5 derivado da chave — um identicon, na linha do que o GitHub usa
 * desde sempre para quem não tem foto.
 *
 * Espelhado porque simetria é o que faz uma mancha de 28px virar uma FORMA que a pessoa
 * reconhece de relance. Padrão assimétrico no mesmo tamanho lê como sujeira.
 *
 * Só as 3 colunas da esquerda são sorteadas; as 2 da direita são reflexo.
 */
export function padraoDeIdenticon(chave) {
  const h = hashDeIdentidade(chave);
  const grade = [];

  for (let y = 0; y < LADO; y += 1) {
    const linha = new Array(LADO).fill(false);
    for (let x = 0; x < METADE; x += 1) {
      // Um bit por célula, tirado de posições diferentes do hash para as linhas não repetirem.
      const bit = (h >>> (y * METADE + x) % 29) & 1;
      linha[x] = bit === 1;
      linha[LADO - 1 - x] = linha[x];
    }
    grade.push(linha);
  }

  return ajustarDensidade(grade);
}
